import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { useSnackbar } from 'baseui/snackbar'

import useModal from '@/shared/hooks/useModal'
import { selectAuthData } from '@/containers/Login/AuthSelectors'

import { websalesUsersManagementActions } from './WebsalesUsersManagementSagas'
import { selectWebsalesUsersManagement } from './WebsalesUsersManagementSelectors'
import { setSelectedWebsalesUser } from './WebsalesUsersManagementSlice'
import { WebsalesUser, WebsalesUsersRoutes } from './types'

interface UseWebsalesUsersManagementReturn {
  websalesUsers: WebsalesUser[]
  modalTitle: string
  websalesUsersListIsLoading: boolean
  websalesUsersError: string | null
  canDelete: boolean
  t: TFunction
  isOpen: boolean
  close: () => void
  setIsOpen: (open: boolean) => void
  revealedPassword: string
  closeRevealedPassword: () => void
  updateWebsalesUser: (websalesUser: WebsalesUser | null) => void
  toggleStatus: (websalesUser: WebsalesUser) => void
  deleteWebsalesUser: (websalesUserId: number | string) => void
  regeneratePassword: (websalesUserId: number | string) => void
}

const useWebsalesUsersManagement = (): UseWebsalesUsersManagementReturn => {
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { extraRoutes } = useSelector(selectAuthData) as {
    extraRoutes?: { websalesUsers?: WebsalesUsersRoutes }
  }
  const websalesUsersRoutes: WebsalesUsersRoutes = extraRoutes?.websalesUsers ?? {}

  const { items, selectedWebsalesUser, isLoading, error } = useSelector(selectWebsalesUsersManagement)

  const { t } = useTranslation()
  const { isOpen, close, setIsOpen } = useModal()
  const [revealedPassword, setRevealedPassword] = useState('')

  const modalTitle =
    selectedWebsalesUser.id === 0
      ? t('websalesUsersManagement:modal.header_create')
      : t('websalesUsersManagement:modal.header_update')

  const updateWebsalesUser = (websalesUser: WebsalesUser | null) => {
    dispatch(
      setSelectedWebsalesUser({
        id: websalesUser?.id ?? 0,
        username: websalesUser?.username ?? '',
        password: '',
        is_active: websalesUser ? websalesUser.is_active : true,
      }),
    )
    setIsOpen(true)
  }

  const toggleStatus = (websalesUser: WebsalesUser) => {
    if (!window.confirm(t('websalesUsersManagement:toggle_status_alert'))) return
    dispatch({
      type: websalesUsersManagementActions.UPDATE_WEBSALES_USER,
      websalesUserData: {
        id: websalesUser.id,
        username: websalesUser.username,
        is_active: !websalesUser.is_active,
      },
      enqueue,
      onSuccess: () => {},
      setFormErrors: () => {},
    })
  }

  const deleteWebsalesUser = (websalesUserId: number | string) => {
    if (!window.confirm(t('websalesUsersManagement:delete_alert'))) return
    dispatch({ type: websalesUsersManagementActions.DELETE_WEBSALES_USER, websalesUserId, enqueue })
  }

  const regeneratePassword = (websalesUserId: number | string) => {
    if (!window.confirm(t('websalesUsersManagement:regenerate_password_alert'))) return
    dispatch({
      type: websalesUsersManagementActions.REGENERATE_WEBSALES_USER_PASSWORD,
      websalesUserId,
      enqueue,
      onSuccess: (password: string) => setRevealedPassword(password),
      onError: () => {},
    })
  }

  useEffect(() => {
    if (!websalesUsersRoutes.list) return
    dispatch({ type: websalesUsersManagementActions.LOAD_LIST, enqueue })
  }, [websalesUsersRoutes.list])

  return {
    websalesUsers: items,
    modalTitle,
    websalesUsersListIsLoading: isLoading,
    websalesUsersError: error,
    canDelete: !!websalesUsersRoutes.delete,
    t,
    isOpen,
    close,
    setIsOpen,
    revealedPassword,
    closeRevealedPassword: () => setRevealedPassword(''),
    updateWebsalesUser,
    toggleStatus,
    deleteWebsalesUser,
    regeneratePassword,
  }
}

export default useWebsalesUsersManagement
